import { ZfDivider, ZfIcon, ZfToggle, ZfTooltip } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddAppendixDocument, useDeleteAppendixDocument, useDownloadAppendixDocument } from '../../api';
import type { ClientPackageAppendixDocumentDto, ClientPackageAppendixDocumentType, ClientPackageDto } from '../../api/generated/client';
import { getSplitClientPackageAppendix, getUpdatedAppendixDocuments } from '../../utils/clientPackageUtils';
import { ClientPackageCheckboxItem } from './ClientPackageCheckboxItem';
import { ClientPackageFileActions } from './ClientPackageFileActions';

const appendixOrder: ClientPackageAppendixDocumentType[] = [
    'BANKABLE_ASSETS',
    'PENSION_ASSETS',
    'REAL_ESTATE_ASSETS',
    'INSURANCE_ASSETS',
    'PARTICIPATION_ASSETS',
    'OTHER_ASSETS',
    'LIABILITIES',
    'CALCULATION_BASIS',
    'LIQUIDITY_NEED_CALCULATION',
    'FAMILY_TREE',
    'LOGISMATA_TAX',
    'LOGISMATA_SAVING',
    'LOGISMATA_PENSION',
    'OTHER',
];

interface IProps {
    clientPackage: ClientPackageDto;
    disabled?: boolean;
    updateClientPackage: (updatedPackage: Partial<ClientPackageDto>) => void;
}

export const ClientPackageAppendixSelector: FunctionComponent<IProps> = ({ clientPackage, disabled, updateClientPackage }) => {
    const { t } = useTranslation();

    const downloadAppendixDocument = useDownloadAppendixDocument();
    const addAppendixDocument = useAddAppendixDocument();
    const deleteAppendixDocument = useDeleteAppendixDocument();

    const onAppendixSelected = useCallback(
        (appendix: ClientPackageAppendixDocumentDto, selected: boolean) => {
            updateClientPackage(getUpdatedAppendixDocuments(clientPackage, appendix, selected));
        },
        [clientPackage, updateClientPackage],
    );

    const onUploadAppendix = async (appendix: ClientPackageAppendixDocumentDto, files: File[]) => {
        const newAppendix = await addAppendixDocument.mutateAsync({ documentId: appendix.documentId, file: files[0] });
        const { appendixDocuments } = getSplitClientPackageAppendix(clientPackage, newAppendix.documentId);
        updateClientPackage({ ...clientPackage, clientPackageAppendixDocuments: [...appendixDocuments, newAppendix] });
    };

    const onDeleteAppendix = async (appendixToDelete: ClientPackageAppendixDocumentDto) => {
        await deleteAppendixDocument.mutateAsync(appendixToDelete.documentId);
        const { appendix, appendixDocuments } = getSplitClientPackageAppendix(clientPackage, appendixToDelete.documentId);
        const updatedAppendixDocuments: ClientPackageAppendixDocumentDto[] = [
            ...appendixDocuments,
            { ...appendix!, selected: false, documentUuid: undefined, filename: undefined },
        ];
        updateClientPackage({ ...clientPackage, clientPackageAppendixDocuments: updatedAppendixDocuments });
    };

    const sortedAppendixDocuments = [...clientPackage.clientPackageAppendixDocuments]
        .filter((appendix) => appendix.type !== 'OTHER')
        .sort((a, b) => appendixOrder.indexOf(a.type) - appendixOrder.indexOf(b.type));

    return (
        <div className="client-package-editor__selector-panel">
            <div className="client-package-editor__selector-panel__title">
                <ZfToggle
                    label={t('clientPackageAppendixMasterToggle')}
                    checked={clientPackage.appendixEnabled}
                    disabled={disabled}
                    onToggleChange={(event: CustomEvent<boolean>) => updateClientPackage({ appendixEnabled: event.detail })}
                />
                <ZfTooltip text={t('clientPackageAppendixInfoTooltip')} side="right">
                    <ZfIcon name="info" size="small" slot="anchor" />
                </ZfTooltip>
            </div>
            <div className="client-package-editor__selector-panel__content">
                {clientPackage.appendixEnabled && (
                    <>
                        {sortedAppendixDocuments
                            .filter((appendix) => !appendix.fileRequired)
                            .map((appendix) => (
                                <ClientPackageCheckboxItem
                                    key={appendix.type.toString()}
                                    label={t(`clientPackageAppendix-${appendix.type.toString()}`)}
                                    isDisabled={!!disabled}
                                    isSelected={appendix.selected}
                                    onChange={(isSelected) => onAppendixSelected(appendix, isSelected)}
                                />
                            ))}
                        <ZfDivider className="client-package-editor__selector-panel__divider" orientation="horizontal" />
                        {sortedAppendixDocuments
                            .filter((appendix) => appendix.fileRequired)
                            .map((appendix) => (
                                <ClientPackageCheckboxItem
                                    key={appendix.type.toString()}
                                    label={t(`clientPackageAppendix-${appendix.type.toString()}`)}
                                    isDisabled={disabled || !appendix.documentUuid || !appendix.filename}
                                    isSelected={appendix.selected}
                                    onChange={(isSelected) => onAppendixSelected(appendix, isSelected)}
                                    actions={
                                        <ClientPackageFileActions
                                            appendix={appendix}
                                            isDisabled={!!disabled}
                                            onFileUpload={(files) => onUploadAppendix(appendix, files)}
                                            onFileDownload={() => downloadAppendixDocument.mutateAsync(appendix.documentId)}
                                            onFileDelete={() => onDeleteAppendix(appendix)}
                                        />
                                    }
                                />
                            ))}
                    </>
                )}
            </div>
        </div>
    );
};
